import { SMB_ALL_QUARTERS, SMB_FY_QUARTER, SMB_QUARTERS } from '../configs'
import { ISmbNode, TSmbSplitStore } from '../model/types'

import { getSmbEffectiveValue, TGetSmbWeight } from './getSmbEffectiveValue'
import { isSmbNodeExcluded } from './isSmbNodeExcluded'
import {
  clearAncestorSplits,
  clearNodeSplits,
  clearSubtreeSplits,
  deleteStoredSplit,
  getDirectStoredSplit,
  hasGenuineDirectSplit,
  hasGenuineQuarterEntry,
  materializeQuarterFallbacks,
  normalizeSmbPercent,
  setStoredSplit,
  subtreeHasGenuineSplit,
} from './smbSplitStore'

export interface IApplySmbEdit {
  node: ISmbNode
  ancestors: ISmbNode[]
  year: number
  fq: number
  smb: number
  getWeight: TGetSmbWeight
  /** Whether the edited year is currently shown and authored as Q1..Q4 plus weighted FY. */
  deriveFyFromQuarters?: boolean
}

/** A coarser edit needs confirmation when it replaces real detail or quarterly entries. */
export const wouldSmbEditDiscardGenuineData = (
  store: TSmbSplitStore,
  node: ISmbNode,
  year: number,
  fq: number
): boolean =>
  (fq === SMB_FY_QUARTER && hasGenuineQuarterEntry(store, node.id, year)) ||
  node.children.some((child) => subtreeHasGenuineSplit(store, child, year))

const getOfferingAncestor = (ancestors: ISmbNode[]): ISmbNode | null =>
  ancestors.find((ancestor) => ancestor.kind === 'offering') || null

/**
 * Offering is a shared default for otherwise independent categories. A nested edit may make
 * its Category/SubCategory roll-up stale, but it must not remove that shared Offering default.
 * The Offering itself is invalidated only by a direct edit on one of its Category children.
 */
const getAncestorsInvalidatedByEdit = (node: ISmbNode, ancestors: ISmbNode[]): ISmbNode[] => {
  const withinCategory = ancestors.filter((ancestor) => ancestor.kind !== 'offering')
  const offering = getOfferingAncestor(ancestors)

  return node.kind === 'category' && offering ? [offering] : withinCategory
}

/**
 * A blur of an unchanged direct value is a no-op. A visually equal roll-up is intentionally
 * not: making that value direct would replace its genuine descendants/ancestors.
 */
export const isSameDirectSmbEdit = (
  store: TSmbSplitStore,
  { node, ancestors, year, fq, smb }: Omit<IApplySmbEdit, 'getWeight'>
): boolean => {
  const direct = getDirectStoredSplit(store, node.id, year, fq)
  if (!direct || direct.isSeeded || normalizeSmbPercent(direct.smb) !== normalizeSmbPercent(smb)) {
    return false
  }

  const wouldClearDescendant = node.children.some((child) =>
    subtreeHasGenuineSplit(store, child, year)
  )
  const wouldInvalidateAncestor = getAncestorsInvalidatedByEdit(node, ancestors).some((ancestor) =>
    hasGenuineDirectSplit(store, ancestor.id, year)
  )

  return !wouldClearDescendant && !wouldInvalidateAncestor
}

/**
 * Applies one authoritative edit and canonicalizes the time grain for that node:
 * - FY edit removes Q1..Q4;
 * - quarter edit materializes FY fallbacks, removes the old FY, then writes a fresh weighted FY.
 */
export const applySmbEditToStore = (
  store: TSmbSplitStore,
  { node, ancestors, year, fq, smb, getWeight, deriveFyFromQuarters = true }: IApplySmbEdit
): void => {
  const normalizedSmb = normalizeSmbPercent(smb)
  const offering = getOfferingAncestor(ancestors)
  const editsCategoryUnderOffering = node.kind === 'category' && !!offering
  const offeringSnapshots =
    editsCategoryUnderOffering && offering && hasGenuineDirectSplit(store, offering.id, year)
      ? (deriveFyFromQuarters && hasGenuineQuarterEntry(store, offering.id, year)
          ? SMB_ALL_QUARTERS
          : [SMB_FY_QUARTER]
        ).flatMap((snapshotFq) => {
          const snapshot = getSmbEffectiveValue(store, offering, year, snapshotFq, getWeight, {
            deriveFyFromQuarters: deriveFyFromQuarters && snapshotFq === SMB_FY_QUARTER,
          })

          return snapshot.isEmpty ? [] : [{ fq: snapshotFq, smb: snapshot.smb }]
        })
      : []

  if (fq === SMB_FY_QUARTER) {
    SMB_QUARTERS.forEach((quarter) => deleteStoredSplit(store, node.id, year, quarter))
  } else {
    materializeQuarterFallbacks(store, node.id, year)
    deleteStoredSplit(store, node.id, year, SMB_FY_QUARTER)
  }

  setStoredSplit(store, node.id, year, fq, normalizedSmb)
  clearSubtreeSplits(store, node, year)
  clearAncestorSplits(
    store,
    ancestors.filter((ancestor) => ancestor.kind !== 'offering'),
    year
  )

  if (editsCategoryUnderOffering) {
    if (offeringSnapshots.length) {
      const bakeOfferingIntoEmptyFrontier = (siblingNode: ISmbNode): void => {
        if (isSmbNodeExcluded(siblingNode)) {
          return
        }

        if (hasGenuineDirectSplit(store, siblingNode.id, year)) {
          return
        }

        if (!subtreeHasGenuineSplit(store, siblingNode, year)) {
          clearNodeSplits(store, siblingNode.id, year)
          clearSubtreeSplits(store, siblingNode, year)
          offeringSnapshots.forEach((snapshot) =>
            setStoredSplit(
              store,
              siblingNode.id,
              year,
              snapshot.fq,
              normalizeSmbPercent(snapshot.smb),
              true
            )
          )

          return
        }

        // This branch is partly planned below. Its empty child frontiers were still inheriting
        // Offering, so preserve only those without overriding the genuine siblings beside them.
        clearNodeSplits(store, siblingNode.id, year)
        siblingNode.children.forEach(bakeOfferingIntoEmptyFrontier)
      }

      offering.children.forEach((sibling) => {
        if (sibling.id === node.id) {
          return
        }

        bakeOfferingIntoEmptyFrontier(sibling)
      })
    }

    clearNodeSplits(store, offering.id, year)
  }

  if (fq !== SMB_FY_QUARTER) {
    const annual = getSmbEffectiveValue(store, node, year, SMB_FY_QUARTER, getWeight, {
      deriveFyFromQuarters: true,
    })

    if (!annual.isEmpty) {
      setStoredSplit(store, node.id, year, SMB_FY_QUARTER, normalizeSmbPercent(annual.smb), true)
    }
  }
}
